import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { IDraftEmail } from '../../../models/email-message';
import { Store } from '@ngxs/store';
import { UserState } from '../../../state/user/user-state';
import { CardModule } from 'primeng/card';
import { AsyncPipe, DatePipe, NgFor, NgIf, SlicePipe } from '@angular/common';
import { PanelModule } from 'primeng/panel';
import { TagModule } from 'primeng/tag';
import { DividerModule } from 'primeng/divider';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { ChipsModule } from 'primeng/chips';
import { FloatLabelModule } from 'primeng/floatlabel';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { EditorModule } from 'primeng/editor';
import { ButtonGroupModule } from 'primeng/buttongroup';
import { DropdownModule } from 'primeng/dropdown';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipModule } from 'primeng/chip';
import { BadgeModule } from 'primeng/badge';
import { DialogModule } from 'primeng/dialog';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { TemplateEmailListComponent } from '../template-email-list/template-email-list.component';
import { MenuItem, Message } from 'primeng/api';
import { MessagesModule } from 'primeng/messages';
import { IEmailHandler } from '../email-handler';
import { HarloweSupport } from '../../../models/support';
import { MenuModule } from 'primeng/menu';
import { take } from 'rxjs';
import { DOMAIN } from '../../../services/injection-tokens';

@Component({
  selector: 'app-email-composer',
  standalone: true,
  imports: [CardModule, SlicePipe, NgIf, NgFor,PanelModule, TagModule,DividerModule, DatePipe, ButtonModule,
    TableModule,TooltipModule, ChipsModule, FloatLabelModule,FormsModule,InputTextModule, OverlayPanelModule,
    EditorModule,ButtonGroupModule, AsyncPipe, DropdownModule, CheckboxModule, ChipModule, DividerModule,
    BadgeModule, DialogModule, MessagesModule, TemplateEmailListComponent, MenuModule],
  templateUrl: './email-composer.component.html',
  styleUrl: './email-composer.component.scss',
})
export class EmailComposerComponent implements OnInit, OnChanges, OnDestroy
{
  // @ViewChild('editor') editor: any;

  @Input() draftEmail?: IDraftEmail = {} as IDraftEmail;
  @Input() emailHandler: IEmailHandler | undefined = undefined;
  @Input() allowAttachments: boolean = true;
  @Output() sendEmail: EventEmitter<IDraftEmail> = new EventEmitter<IDraftEmail>();
  @Output() cancelEmail: EventEmitter<IDraftEmail> = new EventEmitter<IDraftEmail>();
  @Output() draftSaved: EventEmitter<IDraftEmail> = new EventEmitter<IDraftEmail>();

  currentUser$ = this._store.select(UserState.currentUser); 
  messages: Message[] = [];
  requiredFields: boolean = false;

  private hasSent = false;
  private hasSaved = false;
  private _internalDraft: IDraftEmail | undefined;
  // quill: Quill | undefined;
  
  selectedRecipient: any = null;
  selectedCc: any = null;
  selectedBcc: any = null;
  selectedEmail: string | null = null;
  recipientOptions: any = [];

  recipientItems: MenuItem[] = [];
  ccItems: MenuItem[] = [];
  bccItems: MenuItem[] = [];

  get attachmentEntryList(): [string, HarloweSupport.File[]][] 
  {
    if (this.emailHandler?.attachments)
    {
      return Array.from(this.emailHandler.attachments.entries());
    }
    return [];
  }

  constructor(protected _store:Store, @Inject(DOMAIN) private domain:string)
  {
  }

  ngOnChanges(changes: SimpleChanges): void
  {
    if (changes['draftEmail']?.currentValue)
    {
      this._internalDraft = changes['draftEmail'].currentValue;
      this.hasSaved = false;
      this.hasSent = false;
    }
  }

  async ngOnInit(): Promise<void> 
  {
    this.currentUser$.pipe(take(1)).subscribe(currentUser => {
      if (currentUser && currentUser.emailAddresses && currentUser.emailAddresses.length > 0 && this.draftEmail) {
        const tealEmail = currentUser.emailAddresses.find((email: string) => email.includes(this.domain));
        this.draftEmail.from = tealEmail || currentUser.emailAddresses[0];
      }
    });

    if (this.emailHandler && this.emailHandler.emailRecipients)
    {
      for(const [recipientType, recipient] of this.emailHandler?.emailRecipients)
      {
        if (recipient.emailAddresses.length > 0)
        {
          this.recipientOptions.push({ label: recipientType + ' - ' +  recipient.fullName , value: {recipientType, recipient}});
          const r = recipient;
          this.recipientItems = [...this.recipientItems, {label: recipientType + ' - ' + recipient.fullName, command: () => this.selectRecipient(r)}  ]
          this.ccItems = [...this.ccItems, {label: recipientType + ' - ' + recipient.fullName, command: () => this.selectCc(r)}  ]
          this.bccItems = [...this.bccItems, {label: recipientType + ' - ' + recipient.fullName, command: () => this.selectBcc(r)}  ]

        }

      }
    }

    this.messages = [{ severity: 'info', detail: 'Fill all required fields' }];

  }

  trackByLabel(index: number, item: [string, HarloweSupport.File[]])
  {
    return item[0];
  }

  trackByField(index: number, file: HarloweSupport.File)
  {
    return file.id;
  }


  

  selectRecipient(recipient: any)
  {
    if(recipient)
    {
      const email = recipient.emailAddresses[0];
      if (this.draftEmail && !this.draftEmail.recipients.includes(email as string))
      {
        this.draftEmail.recipients = [...this.draftEmail.recipients, email];
      }
    }
    this.selectedRecipient = undefined;
  }

  selectCc(recipient: any)
  {
    if(recipient)
    {
      const email = recipient.emailAddresses[0];
      if (this.draftEmail && !this.draftEmail.ccs.includes(email as string))
      {
        this.draftEmail.ccs = [...this.draftEmail.ccs, email];
      }
    }
    this.selectedCc = undefined;
  }

  selectBcc(recipient: any)
  {
    if(recipient)
    {
      const email = recipient.emailAddresses[0];
      if (this.draftEmail && !this.draftEmail.bccs.includes(email as string))
      {
        this.draftEmail.bccs = [...this.draftEmail.bccs, email];
      }
    }
    this.selectedBcc = undefined;
  }

  

  addRecipientToDraft()
  {
    if (this.draftEmail && !this.draftEmail.recipients.includes(this.selectedEmail as string))
      this.draftEmail.recipients = [...this.draftEmail.recipients, this.selectedEmail as string];
    this.selectedRecipient = null;
    this.selectedEmail = null;
  } 

  templateApplied(draft: IDraftEmail)
  {
    //Don't use this draft wholesale... just extract the subject and body from it
    if(this.draftEmail)
    {
      this.draftEmail.body = draft.body;
      this.draftEmail.subject = draft.subject;
    }
    else
    {
      this.draftEmail = draft;
    }
  }

  ngOnDestroy(): void
  {
    if (!this.hasSent && !this.hasSaved &&
        this._internalDraft?.subject != null &&
        (this._internalDraft?.recipients?.length ?? 0) >= 1)
    {
      this.persistDraft();
    }
  }

  private persistDraft(): void
  {
    if (this._internalDraft)
    {
      if (this.emailHandler && this._internalDraft.draftType == undefined)
        this._internalDraft.draftType = this.emailHandler.draftType;
      this.emailHandler?.saveDraftEmail(this._internalDraft);
      this.draftSaved.emit(this._internalDraft);
      this.hasSaved = true;
    }
  }

  sendButtonPressed(event:MouseEvent)
  {
    this.hasSent = true;
    this.sendEmail.emit(this.draftEmail);
  }

  cancelButtonPressed(event:MouseEvent)
  {
    this.cancelEmail.emit();
    this.draftEmail = undefined;
  }  
    
  allowSend() : boolean
  {
    if (this.draftEmail && (this.containsCurlyBrackets(this.draftEmail.subject) || this.containsCurlyBrackets(this.draftEmail.body)))
    {
      this.requiredFields = true;
      return false
    } 
    else
      this.requiredFields = false;
    return this.draftEmail != null && this.draftEmail.recipients != null  && 
            this.draftEmail.recipients.length > 0 && this.draftEmail.subject != null && 
            this.draftEmail.body != null && this.draftEmail.from != null
  }

  allowSave() : boolean
  {
    return this.draftEmail != null && this.draftEmail.subject != null && this.draftEmail.recipients != null && this.draftEmail.recipients.length >= 1;
  }

  containsCurlyBrackets(inputString: string)
  {
    const pattern = /!\{.*?\}!/;
    return pattern.test(inputString);
  }

  
}

 